/**
 * IrisToast - Themed notification component
 * Features Greek/Iris-inspired icons and dark celestial design
 */

import { useEffect, useState } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface IrisToastProps {
  id: number;
  type: ToastType;
  message: string;
  onClose: () => void;
  duration?: number;
  actionLabel?: string;
  onAction?: (() => void) | null;
}

export default function IrisToast({
  type,
  message,
  onClose,
  duration = 3000,
  actionLabel,
  onAction,
}: IrisToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Fade in animation
    setTimeout(() => setIsVisible(true), 10);

    // Auto-dismiss
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(onClose, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  // Icon and color configuration for each type
  const config = {
    success: {
      icon: '✓',
      iconBg: 'from-emerald-500 to-cyan-500',
      border: 'border-emerald-500/50',
      glow: 'shadow-emerald-500/30',
      text: 'text-brand-tertiary',
      title: 'Divine Success',
    },
    error: {
      icon: '⚠',
      iconBg: 'from-rose-500 to-orange-500',
      border: 'border-rose-500/50',
      glow: 'shadow-rose-500/30',
      text: 'text-brand-secondary',
      title: 'Alert from Olympus',
    },
    warning: {
      icon: '⚡',
      iconBg: 'from-amber-500 to-yellow-500',
      border: 'border-amber-500/50',
      glow: 'shadow-amber-500/30',
      text: 'text-brand-primary-container',
      title: 'Herald Warning',
    },
    info: {
      icon: 'ℹ',
      iconBg: 'from-cyan-500 to-blue-500',
      border: 'border-cyan-500/50',
      glow: 'shadow-cyan-500/30',
      text: 'text-brand-tertiary',
      title: 'Message from Iris',
    },
  };

  const { icon, iconBg, border, glow, text, title } = config[type];

  return (
    <div
      className={`iris-toast transform transition-all duration-300 ${
        isVisible && !isExiting ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      }`}
    >
      {/* Main toast container */}
      <div
        className={`relative overflow-hidden rounded-lg border ${border} ${glow} shadow-xl backdrop-blur-sm`}
        style={{
          background:
            'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(76, 29, 149, 0.3) 50%, rgba(30, 41, 59, 0.95) 100%)',
        }}
      >
        {/* Rainbow shimmer border animation */}
        <div
          className="absolute inset-0 opacity-10 animate-rainbow pointer-events-none"
          style={{
            background:
              'linear-gradient(90deg, #9333ea, #3b82f6, #06b6d4, #10b981, #f59e0b, #9333ea)',
            backgroundSize: '200% 100%',
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex items-start gap-3 p-4 min-w-[300px] max-w-md">
          {/* Greek-inspired icon circle */}
          <div className="flex-shrink-0">
            <div className="relative w-10 h-10">
              {/* Pulsing glow */}
              <div
                className={`absolute inset-0 rounded-full bg-gradient-to-br ${iconBg} opacity-20 animate-pulse-slow blur-sm`}
              />

              {/* Icon container with Greek border pattern */}
              <div
                className={`relative w-10 h-10 rounded-full bg-gradient-to-br ${iconBg} flex items-center justify-center font-bold text-brand-on-primary text-xl border-2 border-white/20 shadow-lg`}
                style={{
                  boxShadow: '0 0 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
                }}
              >
                {icon}
              </div>

              {/* Spinning ring decoration */}
              <div className="absolute -inset-1 rounded-full border border-white/10 animate-spin-slow pointer-events-none" />
            </div>
          </div>

          {/* Message content */}
          <div className="flex-1 pt-0.5">
            <h4 className={`font-semibold text-sm ${text} mb-1`}>{title}</h4>
            <p className="text-brand-on-primary/90 text-sm leading-relaxed">{message}</p>
          </div>

          {/* Action / Close buttons */}
          <div className="flex-shrink-0 flex items-center gap-2">
            {actionLabel && onAction && (
              <button
                onClick={() => {
                  try {
                    onAction();
                  } catch (e) {
                    // ignore
                  }
                  setIsExiting(true);
                  setTimeout(onClose, 300);
                }}
                className="text-sm px-3 py-1 bg-brand-primary text-brand-on-primary rounded"
              >
                {actionLabel}
              </button>
            )}

            {/* Close button */}
            <button
              onClick={() => {
                setIsExiting(true);
                setTimeout(onClose, 300);
              }}
              className="flex-shrink-0 text-brand-on-primary/50 hover:text-brand-on-primary transition-colors"
              aria-label="Close notification"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div
          className={`h-1 bg-gradient-to-r ${iconBg} transform origin-left`}
          style={{
            animation: `shrink ${duration}ms linear`,
          }}
        />

        {/* Sparkle particles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-brand-primary/60 animate-float"
              style={{
                left: `${20 + i * 30}%`,
                top: `${30 + i * 20}%`,
                animationDelay: `${i * 0.3}s`,
                animationDuration: `${3 + i}s`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
